import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  useTheme,
  useMediaQuery,
  Divider,
  Fade,
  Tooltip,
  ListItemIcon,
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Close as CloseIcon, 
  KeyboardArrowDown,
  Home as HomeIcon,
  Info as AboutIcon,
  ContactSupport as ContactIcon,
  Article as BlogIcon,
  Discount as ServicesIcon,
  EventAvailable as ConsultationIcon,
} from '@mui/icons-material';
import Logo from './Logo';

const navItems = [
  { text: 'Home', path: '/', icon: HomeIcon },
  { text: 'Services', path: '/services', icon: ServicesIcon },
  { text: 'Blog', path: '/blog', icon: BlogIcon },
  { text: 'About', path: '/about', icon: AboutIcon },
  { text: 'Contact', path: '/contact', icon: ContactIcon },
];

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') {
      return false;
    }
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        py: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.primary.dark,
        color: 'white',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Logo color="light" />
      </Box>
      <Divider sx={{ background: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.text}
            component={RouterLink}
            to={item.path}
            sx={{
              color: 'white',
              textDecoration: 'none',
              padding: '12px 24px',
              transition: 'all 0.3s ease',
              backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
              <item.icon />
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: isActive(item.path) ? 600 : 400,
                fontSize: '1.1rem',
                letterSpacing: '0.5px',
              }}
            />
          </ListItem>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Button
          component={RouterLink}
          to="/consultation"
          variant="contained"
          color="secondary"
          fullWidth
          startIcon={<ConsultationIcon />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            letterSpacing: '0.5px',
            boxShadow: '0 4px 14px rgba(225, 122, 71, 0.25)',
          }}
        >
          Free Consultation
        </Button>
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={scrolled ? 2 : 0}
      sx={{
        bgcolor: 'white',
        borderBottom: scrolled ? 'none' : '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        py: scrolled ? (isSmallMobile ? 0.3 : 0.5) : (isSmallMobile ? 0.5 : 1),
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <RouterLink to="/" style={{ textDecoration: 'none' }}>
            <Fade in={true} timeout={800}>
              <Box>
                <Logo />
              </Box>
            </Fade>
          </RouterLink>

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="primary"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                display: { md: 'none' },
                '& .MuiSvgIcon-root': {
                  fontSize: isSmallMobile ? 24 : 28,
                },
              }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.text}
                  component={RouterLink}
                  to={item.path}
                  color="primary"
                  sx={{
                    position: 'relative',
                    fontWeight: 500,
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 0,
                    color: theme.palette.text.primary,
                    letterSpacing: '0.5px',
                    transition: 'all 0.3s ease',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 8,
                      left: '50%',
                      width: isActive(item.path) ? '30%' : '0%',
                      height: 3,
                      backgroundColor: theme.palette.secondary.main,
                      transform: isActive(item.path) ? 'translateX(-50%)' : 'translateX(-50%)',
                      transition: 'width 0.3s ease-in-out',
                      borderRadius: '2px',
                    },
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: theme.palette.primary.main,
                      '&::after': {
                        width: '30%',
                      },
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Tooltip title="Book a free consultation">
                <Button
                  component={RouterLink}
                  to="/consultation"
                  variant="contained"
                  color="secondary"
                  endIcon={<KeyboardArrowDown />}
                  sx={{
                    ml: 2,
                    px: 3,
                    py: 1.2,
                    borderRadius: '30px',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    boxShadow: '0 4px 14px rgba(225, 122, 71, 0.25)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 18px rgba(225, 122, 71, 0.35)',
                    },
                  }}
                >
                  Free Consultation
                </Button>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: { xs: '85%', sm: '75%' },
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar; 